import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPOSReport } from '../../api/distributionService';
import { formatDate, formatCurrency } from '../../utils/formatters';

interface POSDashboardProps {
  timeRange?: 'day' | 'week' | 'month' | 'year';
}

export default function POSDashboard({ timeRange = 'day' }: POSDashboardProps) {
  const { locationId } = useParams<{ locationId: string }>();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const now = new Date();
        let startDate = new Date();

        switch (timeRange) {
          case 'day':
            startDate.setDate(now.getDate() - 1);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }

        const data = await getPOSReport(locationId!, startDate, now);
        setReport(data);
      } catch (err) {
        setError('Failed to load POS data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (locationId) loadData();
  }, [locationId, timeRange]);

  if (loading) return <div className="p-4">Loading POS data...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!report) return <div className="p-4">No POS data available</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard 
          title="Total Transactions" 
          value={report.count} 
          icon="🧾"
        />
        <KPICard 
          title="Total Amount" 
          value={report.total} 
          format={formatCurrency}
          icon="💰"
        />
        <KPICard 
          title="Total Charges" 
          value={report.data.reduce((sum: number, t: any) => sum + t.charges, 0)} 
          format={formatCurrency}
          icon="💸"
        />
      
      </div>

      {/* Transaction Type Summary */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Transaction Type Summary</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          {report.transactionTypes.map((type: any) => (
            <TransactionTypeCard 
              key={type.transactionType}
              type={type.transactionType}
              count={type.count}
              total={type.totalAmount}
            />
          ))}
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Recent Transactions</h2>
          <p className="text-sm text-gray-500">
            Showing {report.count} transactions totaling {formatCurrency(report.total)}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Date</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Amount</TableHeader>
                <TableHeader>Charges</TableHeader>
                <TableHeader>Total</TableHeader>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.data.map((transaction: any) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Sub-components
interface KPICardProps {
  title: string;
  value: number;
  format?: (value: number) => string;
  icon: string;
}

function KPICard({ title, value, format = (v) => v.toString(), icon }: KPICardProps) {
  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="mt-2 text-2xl font-semibold">
        {format(value)}
      </p>
    </div>
  );
}

function TransactionTypeCard({ type, count, total }: { type: string; count: number; total: number }) {
  return (
    <div className="bg-white p-4 rounded-lg border">
      <h3 className="text-sm font-medium text-gray-500">{type}</h3>
      <div className="mt-2 flex justify-between">
        <span className="text-sm">Count: {count}</span>
        <span className="text-sm font-medium">{formatCurrency(total)}</span>
      </div>
    </div>
  );
}

function TableHeader({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

function TransactionRow({ transaction }: { transaction: any }) {
  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(transaction.transactionDate)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {transaction.transactionType}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {transaction.customerId === '00000000-0000-0000-0000-000000000000' ? 'Walk-in' : 'Customer'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatCurrency(transaction.amount)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatCurrency(transaction.charges)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {formatCurrency(transaction.totalAmount)}
      </td>
    </tr>
  );
}